import { flow, Instance, types } from "mobx-state-tree";
import { FileUploadStore } from "@fidget/common/models/files/FileUploadStore";
import { IFileUploadInfo } from "@fidget/common/models/fileUpload";
import { logger } from "@fidget/common/utils";
import { isValidText } from "@fidget/common/utils/isValidText";
import { servicesGetters, uiStoreGetter } from "../utils";
import { IIntentSuggestion, IntentResponseContext } from "../intent/IntentStore";
import { IntentDto } from "@closerplatform/spinner-openapi";
import { IAttachment } from "@fidget/common/components/mstchat/chatMessage";

export enum InputType {
  Message = "message",
  Note = "note"
}

export const ChatInput = types
  .model("ChatInput", {
    messageText: "",
    noteText: "",
    activeInputType: types.optional(
      types.enumeration<InputType>("ActiveInputType", Object.values(InputType)),
      InputType.Message
    ),
    foundProfanity: types.maybe(types.string),
    fileStore: FileUploadStore
  })
  .views(uiStoreGetter)
  .views(servicesGetters)
  .extend((self) => {
    const actions = {
      /**
       * Set message text
       * @param {string} value message text
       */
      setMessageText(value: string) {
        self.messageText = value;
      },

      /**
       * Set note text
       * @param {string} value note text
       */
      setNoteText(value: string) {
        self.noteText = value;
      },

      /**
       * Append text to currently active input type.
       */
      appendText(value: string) {
        if (self.activeInputType === InputType.Note) {
          self.noteText += value;
        } else {
          self.messageText += value;
        }
      },

      /**
       * Set the active, currently edited input type (text message, note).
       * @param {ChatInputType} type  message or note
       */
      setInputType(type: typeof self.activeInputType) {
        self.activeInputType = type;
      },

      /**
       * Attach file to current input
       * @param {File} file low-level File object
       */
      attachFile(file: File, roomId: string) {
        self.fileStore.createFileUpload(file, roomId);
      },

      /**
       * Cancel uploading (attaching) of a file
       * @param {IFileUploadInfo} fileInfo reference to uploaded file
       */
      cancelAttachFile(fileInfo: IFileUploadInfo) {
        self.fileStore.removeFileUpload(fileInfo);
      },

      /**
       * Re-start file upload for eg. failed uploads
       * @param {IFileUploadInfo} fileInfo reference to uploaded file
       */
      retryAttachFile(fileInfo: IFileUploadInfo) {
        self.fileStore.startFileUpload(fileInfo);
      },

      /**
       * Callback for FileStore, will be called on
       * @param {File} file  FileReader's object
       */
      onFile: flow(function* (file: File, roomId: string) {
        const attachment = yield self.spinnerService.uploadFile(file, roomId);
        return attachment;
      }),

      onDeleteFile: flow(function* (fileUpload: IFileUploadInfo) {
        return yield self.spinnerService.deleteFile(fileUpload);
      }),

      /**
       * Clear all properties related to message input eg after sending the mesage
       */
      clearMessageInput() {
        self.messageText = "";
        self.fileStore.clear();
      },

      /**
       * Clear all propproperties related to note input eg after submitting the note
       */
      clearNoteInput() {
        self.noteText = "";
      },

      fillFromIntentDto(intentDto: IntentDto) {
        if (self.activeInputType === InputType.Message) {
          const { context, response } = intentDto;
          actions.fillFrom(response, context as IAttachment[]);
        }
      },

      fillFromIntent(intent: IIntentSuggestion, responseContext: IntentResponseContext) {
        if (self.activeInputType === InputType.Message) {
          const { context, getCompiledResponse } = intent;
          const response = getCompiledResponse(responseContext);
          actions.fillFrom(response, context as IAttachment[]);
        }
      },

      fillFrom(response?: string, context?: IAttachment[]) {
        if (context) {
          try {
            self.fileStore.setIntentAttachments(context);
          } catch (e) {
            logger.error({ message: "Couldn't set chat attachments from intent", error: e });
            self.uiStore.addGenericErrorToast();
          }
        }
        if (response) {
          actions.setMessageText(response);
        }
      },

      setFoundProfanity(value: string) {
        self.foundProfanity = value;
      },

      unsetFoundProfanity() {
        self.foundProfanity = undefined;
      }
    };

    const views = {
      /**
       * Get the text content with respect to selected item type (message, note)
       */
      get inputText() {
        return self.activeInputType === InputType.Note ? self.noteText : self.messageText;
      },

      /**
       * Whether or not the data provided in input is ok to send ie. the message is valid,
       * files have finished uploading (if any have been added) etc.
       */
      get isReadyToSend() {
        if (self.activeInputType === InputType.Note) {
          return isValidText(self.noteText);
        } else {
          return self.fileStore.hasFiles
            ? self.fileStore.isUploadCompleted
            : isValidText(self.messageText);
        }
      },

      get fileUploads() {
        return self.fileStore.fileUploads;
      },

      get hasFiles() {
        return self.fileStore.hasFiles;
      },

      get attachments() {
        return self.fileStore.attachments;
      }
    };

    return { actions, views };
  });

export type IChatInput = Instance<typeof ChatInput>;
